// frontend/app/components/lessonPlans/authoring/TopicAnchorPicker.js
//
// Picks the curriculum anchor a lesson attaches to: level > subject > chapter >
// topic. Reads the curriculum tree the app has already loaded rather than
// fetching its own, so the author sees exactly the tree they browse elsewhere.
//
// The anchor is not cosmetic. `board_topic_id` decides which topic tile the
// lesson belongs to, and `source_topic_id` is what asset references resolve
// against — get it wrong and every PWO scene in the lesson fails to resolve.

import React, { useMemo } from "react";
import { ScrollView, StyleSheet, Text, TouchableOpacity, View } from "react-native";

const labelOf = (node, translate = (key) => key) => {
  const named = String(
    node?.display_name || node?.displayName || node?.name || ""
  ).trim();
  if (named) return named;

  // Curriculum levels carry no name — /api/curriculum_with_images returns
  // { id, grade, subjects } — so fall back to the grade number. Without this
  // every row in the Grade column renders blank but still selectable.
  if (node?.grade !== null && node?.grade !== undefined && node.grade !== "") {
    return translate("Grade {grade}").replace("{grade}", String(node.grade));
  }
  return "";
};

const Column = ({ colors, title, options, selectedId, onSelect, emptyLabel }) => (
  <View style={[styles.column, { borderColor: colors.border }]}>
    <Text style={[styles.columnTitle, { color: colors.textMuted }]}>{title}</Text>
    <ScrollView style={styles.columnScroll} showsVerticalScrollIndicator={false}>
      {options.length === 0 ? (
        <Text style={[styles.empty, { color: colors.textMuted }]}>{emptyLabel}</Text>
      ) : (
        options.map((option) => {
          const selected = option.id === selectedId;
          return (
            <TouchableOpacity
              key={String(option.id)}
              onPress={() => onSelect(option)}
              accessibilityRole="button"
              accessibilityState={{ selected }}
              accessibilityLabel={option.label}
              style={[
                styles.option,
                {
                  backgroundColor: selected ? colors.accentGlow : "transparent",
                  borderColor: selected ? colors.accentPrimary : "transparent",
                },
              ]}
            >
              <Text
                numberOfLines={2}
                style={[
                  styles.optionText,
                  {
                    color: selected ? colors.textPrimary : colors.textSecondary,
                    fontWeight: selected ? "700" : "500",
                  },
                ]}
              >
                {option.label}
              </Text>
            </TouchableOpacity>
          );
        })
      )}
    </ScrollView>
  </View>
);

const TopicAnchorPicker = ({
  colors,
  curriculum = [],
  value = {},
  onChange,
  // A Discovery Journey hangs off a chapter, not a topic — its loops each
  // carry their own. Stopping the picker there rather than writing a second
  // one keeps both anchors browsing the same tree.
  depth = "topic",
  t,
}) => {
  const translate = t || ((key) => key);
  const toTopic = depth !== "chapter";

  const levels = useMemo(
    () => (Array.isArray(curriculum) ? curriculum : []),
    [curriculum]
  );

  const level = levels.find((l) => l.id === value.level_id) || null;
  const subjects = level?.subjects || [];
  const subject = subjects.find((s) => s.id === value.subject_id) || null;
  const chapters = subject?.chapters || [];
  const chapter = chapters.find((c) => c.id === value.chapter_id) || null;
  const topics = chapter?.topics || [];

  const toOptions = (nodes) =>
    (nodes || []).map((node) => ({ id: node.id, label: labelOf(node, translate), node }));

  // Each selection clears everything below it — a chapter from one subject
  // paired with a topic from another would produce an anchor that resolves to
  // nothing, and the failure would only surface at runtime.
  const selectLevel = (option) =>
    onChange({ level_id: option.id });

  const selectSubject = (option) =>
    onChange({
      level_id: value.level_id,
      subject_id: option.id,
      subject_name: option.label,
    });

  const selectChapter = (option) =>
    onChange({
      level_id: value.level_id,
      subject_id: value.subject_id,
      subject_name: value.subject_name,
      chapter_id: option.id,
      chapter_name: option.label,
    });

  const selectTopic = (option) =>
    onChange({
      ...value,
      board_topic_id: option.id,
      source_topic_id: option.node?.source_id ?? null,
      topic_name: option.label,
    });

  return (
    <View>
      <View style={styles.columns}>
        <Column
          colors={colors}
          title={translate("Grade")}
          options={toOptions(levels)}
          selectedId={value.level_id}
          onSelect={selectLevel}
          emptyLabel={translate("No curriculum loaded")}
        />
        <Column
          colors={colors}
          title={translate("Subject")}
          options={toOptions(subjects)}
          selectedId={value.subject_id}
          onSelect={selectSubject}
          emptyLabel={translate("Pick a grade")}
        />
        <Column
          colors={colors}
          title={translate("Chapter")}
          options={toOptions(chapters)}
          selectedId={value.chapter_id}
          onSelect={selectChapter}
          emptyLabel={translate("Pick a subject")}
        />
        {toTopic ? (
          <Column
            colors={colors}
            title={translate("Topic")}
            options={toOptions(topics)}
            selectedId={value.board_topic_id}
            onSelect={selectTopic}
            emptyLabel={translate("Pick a chapter")}
          />
        ) : null}
      </View>

      {!toTopic && value.chapter_id ? (
        <View
          style={[
            styles.summary,
            { backgroundColor: colors.correctBg, borderColor: colors.correctBorder },
          ]}
        >
          <Text style={[styles.summaryText, { color: colors.textPrimary }]}>
            {[value.subject_name, value.chapter_name].filter(Boolean).join("  ›  ")}
          </Text>
          <Text style={[styles.summaryMeta, { color: colors.textMuted }]}>
            {translate("Chapter id")} {value.chapter_id}
          </Text>
        </View>
      ) : null}

      {toTopic && value.board_topic_id ? (
        <View
          style={[
            styles.summary,
            { backgroundColor: colors.correctBg, borderColor: colors.correctBorder },
          ]}
        >
          <Text style={[styles.summaryText, { color: colors.textPrimary }]}>
            {[value.subject_name, value.chapter_name, value.topic_name]
              .filter(Boolean)
              .join("  ›  ")}
          </Text>
          {/* Surfaced because a topic with no source id cannot resolve assets,
              and the author should learn that here rather than at runtime. */}
          <Text style={[styles.summaryMeta, { color: colors.textMuted }]}>
            {translate("Topic id")} {value.board_topic_id}
            {value.source_topic_id
              ? ` · ${translate("source")} ${value.source_topic_id}`
              : ` · ${translate("no source id — assets may not resolve")}`}
          </Text>
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  columns: { flexDirection: "row", gap: 8 },
  column: {
    flex: 1,
    minWidth: 0,
    height: 210,
    borderWidth: 1,
    borderRadius: 10,
    padding: 8,
  },
  columnTitle: {
    fontSize: 10,
    fontWeight: "800",
    letterSpacing: 0.6,
    textTransform: "uppercase",
    marginBottom: 6,
  },
  columnScroll: { flex: 1 },
  option: {
    borderRadius: 7,
    borderWidth: 1,
    paddingHorizontal: 8,
    paddingVertical: 7,
    marginBottom: 3,
  },
  optionText: { fontSize: 12, lineHeight: 16 },
  empty: { fontSize: 11, fontStyle: "italic", paddingHorizontal: 4, paddingTop: 4 },

  summary: {
    marginTop: 10,
    borderRadius: 10,
    borderWidth: 1,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  summaryText: { fontSize: 13, fontWeight: "600" },
  summaryMeta: { fontSize: 11, marginTop: 3 },
});

export default TopicAnchorPicker;
